import {
  Drawer,
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  IconButton,
  Button,
  LinearProgress,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import BugReportIcon from "@mui/icons-material/BugReport";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@context/AuthContext";
import { useThemeMode } from "@context/ThemeContext";
import { useAppSelector } from "@/store";
import { useLanguageToggle } from "@hooks/useLanguageToggle";
import { AppMark } from "@components/AppMark";
import { MAX_USER_STORAGE_FILES } from "@/constants/app";
import { DRAWER_WIDTH, NAV_ITEMS } from "./constants";

interface SidebarProps {
  mobileOpen: boolean;
  onMobileClose: () => void;
}

export function Sidebar({ mobileOpen, onMobileClose }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { isAuthenticated, logout } = useAuth();
  const { mode, toggleTheme } = useThemeMode();
  const { total } = useAppSelector((s) => s.files);
  const { toggleLanguage } = useLanguageToggle();

  const visibleItems = NAV_ITEMS.filter((item) => !item.requiresAuth || isAuthenticated);

  const handleNav = (path: string) => {
    navigate(path);
    onMobileClose();
  };

  const handleLogout = async () => {
    await logout();
    navigate("/account");
    onMobileClose();
  };

  const drawerContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box
        sx={{ p: 3, display: "flex", alignItems: "center", gap: 1.5, cursor: "pointer" }}
        onClick={() => handleNav("/")}
      >
        <AppMark size={32} fontSize={14} borderRadius={2} />
        <Typography
          variant="h6"
          sx={{
            fontWeight: "bold",
            lineHeight: 1,
          }}
        >
          {t("app.name")}
        </Typography>
      </Box>

      <List sx={{ flex: 1, px: 1.5 }}>
        {visibleItems.map((item) => (
          <ListItemButton
            key={item.path}
            selected={location.pathname === item.path}
            onClick={() => handleNav(item.path)}
            sx={{ mb: 0.5 }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <item.icon
                sx={{
                  color: location.pathname === item.path ? "secondary.main" : "text.secondary",
                }}
              />
            </ListItemIcon>
            <ListItemText
              primary={t(item.i18nKey)}
              slotProps={{ primary: { sx: { fontSize: 14, fontWeight: 500 } } }}
            />
          </ListItemButton>
        ))}
        <ListItemButton
          component="a"
          href="https://bugs.pinehood.tech/"
          target="_blank"
          rel="noopener noreferrer"
          sx={{ mb: 0.5 }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <BugReportIcon sx={{ color: "text.secondary" }} />
          </ListItemIcon>
          <ListItemText
            primary={t("nav.reportBug")}
            slotProps={{ primary: { sx: { fontSize: 14, fontWeight: 500 } } }}
          />
        </ListItemButton>
      </List>

      {isAuthenticated && (
        <Box
          sx={{
            mx: 2,
            mb: 2,
            p: 2,
            bgcolor: mode === "light" ? "#EFF6FF" : "#1E293B",
            borderRadius: 3,
            border: "1px solid",
            borderColor: mode === "light" ? "#BFDBFE" : "#334155",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: "secondary.main",
              }}
            >
              {t("storage.title")}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
              }}
            >
              {t("storage.filesUsed", { used: total, max: MAX_USER_STORAGE_FILES })}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={Math.min((total / MAX_USER_STORAGE_FILES) * 100, 100)}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: mode === "light" ? "#BFDBFE" : "#334155",
              "& .MuiLinearProgress-bar": { bgcolor: "secondary.main", borderRadius: 4 },
            }}
          />
        </Box>
      )}

      <Divider />
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
        }}
      >
        <IconButton
          onClick={toggleTheme}
          size="small"
          title={mode === "dark" ? "Light mode" : "Dark mode"}
        >
          {mode === "dark" ? <Brightness7Icon /> : <Brightness4Icon />}
        </IconButton>
        <Button size="small" onClick={toggleLanguage} sx={{ minWidth: 40, fontWeight: 600 }}>
          {i18n.language === "en" ? "HR" : "EN"}
        </Button>
      </Box>

      {isAuthenticated && (
        <>
          <Divider />
          <Box sx={{ p: 1.5 }}>
            <ListItemButton onClick={handleLogout} sx={{ color: "error.main" }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <LogoutIcon color="error" />
              </ListItemIcon>
              <ListItemText
                primary={t("nav.signOut")}
                slotProps={{ primary: { sx: { fontSize: 14, fontWeight: 500 } } }}
              />
            </ListItemButton>
          </Box>
        </>
      )}
    </Box>
  );

  return (
    <>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", lg: "none" },
          "& .MuiDrawer-paper": { width: DRAWER_WIDTH },
        }}
      >
        {drawerContent}
      </Drawer>

      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", lg: "block" },
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            borderRight: "1px solid",
            borderColor: "divider",
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </>
  );
}
